'use client'

import type { RefObject } from 'react'
import cn from 'classnames'
import Link from 'next/link'
import { useTranslations } from 'next-intl'

import shared from '@booking/index.module.scss'
import styles from '../index.module.scss'

import ErrorBox from '../../../components/ErrorBox'
import BackButton from '@booking/components/BackButton'
import TextField from '@booking/components/FormFields/TextField'
import CheckBoxField from '@booking/components/FormFields/CheckboxField'

import PlusIcon from '../plus-icon.svg'

import type { CustomerFormFields } from '../types'
//import { useBookingState } from '@booking/state/booking.context'
import { useBookingExternal } from '@booking/state/external.context'
import { useBookingState } from '../../../state/booking.context'
import { formatMainLink } from '@/src/lib/link'

type CustomerDetailsFormProps = {
  headingRef: RefObject<HTMLHeadingElement | null>
  hideSessionTermsCheckbox: boolean
  errorMessages: string[]
  hasError: (fieldName: string) => boolean
  onComplete: () => void
  onFillData: () => void
  onShowVatField: () => void
  onValueChange: <K extends keyof CustomerFormFields>(
    field: K,
    value: CustomerFormFields[K],
  ) => void
  showVatField: boolean
  values: CustomerFormFields
}

export default function CustomerDetailsForm({
  headingRef,
  hideSessionTermsCheckbox,
  errorMessages,
  hasError,
  onFillData,
  onShowVatField,
  onValueChange,
  showVatField,
  values,
}: CustomerDetailsFormProps) {
  const t = useTranslations('booking')
  const { error, config } = useBookingState()
  const { countries, externalData, locale } = useBookingExternal()

  return (
    <form className={cn(styles.form)} onSubmit={(event) => event.preventDefault()}>
      {error ? <ErrorBox error={error} /> : null}

      <BackButton />

      <h2 ref={headingRef}>{t('ticket.customerDetails.title')}</h2>

      {process.env.NODE_ENV !== 'production' ? (
        <button type="button" onClick={onFillData} className={cn(shared.button, 'mb-4')}>
          Fill data
        </button>
      ) : null}

      {errorMessages.length ? (
        <div className={cn(shared.errorMessage, styles.errorBox)}>
          <ul>
            {errorMessages.map((message, index) => (
              <li key={`${message}-${index}`}>{message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <fieldset>
        <div className="flex gap-x-3">
          <div className="control-group flex-1">
            <TextField
              name="firstName"
              label={t('ticket.customerDetails.firstName')}
              value={values.firstName}
              onChange={(event) => onValueChange('firstName', event.target.value)}
              hasError={hasError('firstName')}
            />
          </div>

          <div className="control-group flex-1">
            <TextField
              name="lastName"
              label={t('ticket.customerDetails.lastName')}
              value={values.lastName}
              onChange={(event) => onValueChange('lastName', event.target.value)}
              hasError={hasError('lastName')}
            />
          </div>
        </div>

        <div className="control-group">
          <TextField
            name="email"
            label={t('ticket.customerDetails.emailAddress')}
            value={values.email}
            onChange={(event) => onValueChange('email', event.target.value.toLowerCase())}
            hasError={hasError('email')}
          />
        </div>

        <div className="control-group">
          <TextField
            type="email"
            name="confirmEmail"
            label={t('ticket.customerDetails.emailConfirm')}
            value={values.emailConfirm}
            onPaste={(event) => event.preventDefault()}
            onChange={(event) => onValueChange('emailConfirm', event.target.value)}
            hasError={hasError('emailConfirm')}
          />
        </div>

        <div className="flex gap-x-3">
          <div className="control-group flex-1">
            <label className="sr-only">{t('ticket.customerDetails.country')}</label>
            <select
              name="country"
              value={values.country}
              className={cn(values.country && styles.selected, hasError('country') && styles.error)}
              onChange={(event) => onValueChange('country', event.target.value)}
            >
              <option value="">{t('ticket.customerDetails.country')}</option>
              {countries.map((item) => (
                <option value={item.code} key={item.code}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          <div className="control-group flex-1">
            <TextField
              name="phone"
              label={t('ticket.customerDetails.phoneNumber')}
              value={values.phone}
              onChange={(event) => onValueChange('phone', event.target.value)}
              hasError={hasError('phone')}
            />
          </div>
        </div>

        <div className="control-group">
          {!showVatField ? (
            <button type="button" className={cn(styles.vatBox)} onClick={onShowVatField}>
              <PlusIcon /> {t('ticket.customerDetails.addVatNumber')}
            </button>
          ) : (
            <TextField
              name="vatNumber"
              label={t('ticket.customerDetails.vatNumber')}
              value={values.vatNumber || ''}
              onChange={(event) => onValueChange('vatNumber', event.target.value)}
              hasError={hasError('vatNumber')}
            />
          )}
        </div>

        {!hideSessionTermsCheckbox ? (
          <CheckBoxField
            label={t.rich('ticket.customerDetails.sessionTerms', {
              termsLink: (chunks) => (
                <Link
                  href={formatMainLink(externalData?.termsPath ?? 'terms', locale)}
                  target="_blank"
                  className="underline"
                >
                  {chunks}
                </Link>
              ),
            })}
            name="sessionTerms"
            checked={values.acceptSessionTerms}
            onChange={() => onValueChange('acceptSessionTerms', !values.acceptSessionTerms)}
            hasError={hasError('acceptSessionTerms')}
          />
        ) : null}

        <CheckBoxField
          label={t.rich('ticket.customerDetails.termsAgree', {
            termsLink: (chunks) => (
              <Link
                href={formatMainLink(externalData?.termsPath ?? 'terms', locale)}
                target="_blank"
                className="underline"
              >
                {chunks}
              </Link>
            ),
          })}
          name="terms"
          checked={values.acceptTerms}
          onChange={() => onValueChange('acceptTerms', !values.acceptTerms)}
          hasError={hasError('acceptTerms')}
        />

        {config.context === 'rock-in-rio' && (
          <CheckBoxField
            label={t.rich('rockInRio.termsAgree', {
              termsLink: (chunks) => (
                <Link
                  href="https://rockinriolisboa.pt/pt/termos-e-condicoes"
                  target="_blank"
                  className="underline"
                >
                  {chunks}
                </Link>
              ),
            })}
            name="rirTerms"
            checked={values.rirTerms ?? false}
            onChange={() => onValueChange('rirTerms', !values.rirTerms)}
            hasError={hasError('rirTerms')}
          />
        )}

        <CheckBoxField
          label={t('ticket.customerDetails.commsAgree')}
          name="comms"
          checked={values.acceptCommunication}
          onChange={(event) => onValueChange('acceptCommunication', event.target.checked)}
        />
      </fieldset>
    </form>
  )
}
